import type { Collaborator } from '@components/ArtBoard/Collaboration/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { socket } from '@store/socketSaga'
import { type MutableRefObject, useEffect } from 'react'
import { useSelector } from 'react-redux'

import useSpyMode from '@/hooks/useSpyMode'
import type { AppState, AuthState } from '@/types'

export const useSocketListeners = (
  currentSocketId: MutableRefObject<string | null>,
  updateCollaborators: (collaborators: Collaborator[]) => void,
  updateSingleCollaborator: (collaborator: Collaborator) => void
) => {
  const projectId = useSelector(migrationProjectSelector.getProjectId)
  const auth = useSelector<AppState, AuthState>((state: AppState) => state.auth)

  const { isSpyMode } = useSpyMode()

  if (socket.id) {
    currentSocketId.current = socket.id
  }

  useEffect(() => {
    const handleConnect = () => {
      if (socket.id) {
        currentSocketId.current = socket.id
      }
    }

    socket.on('connect', handleConnect)

    handleConnect()

    return () => {
      socket.off('connect', handleConnect)
    }
  }, [])

  useEffect(() => {
    if (!projectId) {
      return
    }

    const currentViewer = auth
      ? {
          userId: auth.id,
          color: auth.color,
          avatar: auth.avatar,
          firstName: auth.firstName,
          lastName: auth.lastName,
        }
      : null

    const joinProject = () => {
      if (isSpyMode) {
        return
      }

      socket.emit(
        'project:join',
        {
          projectId,
          currentViewer,
        },
        (collaborators: Collaborator[]) => {
          updateCollaborators(collaborators)
        }
      )
    }

    joinProject()
    socket.on('connect', joinProject)

    socket.on(
      `project:collaborator:${projectId}`,
      (collaborator: Collaborator) => {
        updateSingleCollaborator(collaborator)
      }
    )

    socket.on(
      `project:collaborators:${projectId}`,
      (collaborators: Collaborator[]) => {
        updateCollaborators(collaborators)
      }
    )

    return () => {
      if (!projectId) {
        return
      }

      socket.off('connect', joinProject)
      socket.off(`project:collaborator:${projectId}`)
      socket.off(`project:collaborators:${projectId}`)
    }
  }, [
    projectId,
    updateCollaborators,
    updateSingleCollaborator,
    auth,
    isSpyMode,
  ])
}
